// Phase 3 (api-phase3.md): the AI agent in the event workspace, where the prototype puts it.
//   AgentTab    the Agent context tab (the only place the open event shows the agent; 2026-10-28 there is no strip
//               under the event header): status, level, pause / resume, verdict, handoff, proposed disposition, the
//               step-by-step trace (steps and tool calls merged in time order) and usage (prototype agentPanelHTML).
// At a Level 0 site operators get `{ visible: false }`: a neutral note, nothing about the agent's assessment.
// Verdict chips for queue cards, tabs, the dock and the header are here too. Evidence frames are timeline markers
// (and thumbnails in the Agent tab).
// Phase 4 (api-phase4.md): the tab has approval cards with a countdown, the command box with quick instructions and
// the conversation (instructions and the agent's replies), the drafts, approval / instruction rows in the trace, and
// the AI watches list (Cancel, Add watch, dev Simulate detection). Matched pain points are violet chips (prototype intelHTML).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, errorText, type AgentPanelFull, type AgentToolCall, type AiVerdictSummary, type Approval, type EventSummary, type Instruction } from '../api.ts';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { ApprovalCard, approvalArgs, type PendingApproval } from '../live/copilot.tsx';
import { fmtTime, tzAbbr } from '../time.ts';
import {
  AI_STATES,
  APPROVAL_STATUS_LABEL,
  askWords,
  callWords,
  deviceAsk,
  INSTRUCTION_STATUS_LABEL,
  levelLabel,
  mergeTrace,
  money,
  nowLine,
  pct,
  QUICK_INSTRUCTIONS,
  reasonWords,
  runStatusLabel,
  stateHint,
  stateLabel,
  STEP_KIND_LABEL,
  toolResultLine,
  verdictCls,
  verdictLabel,
  type TraceRow,
} from './ai.ts';
import type { Ctl } from './ctl.ts';
import { useDraft } from './drafts.ts';
import { human, plural } from './util.ts';
import { WatchList } from './Watches.tsx';

/* ---------- small pieces used across the console ---------- */

/** "SUSPICIOUS 74%" in the verdict's severity colour (prototype .verdict). */
export function VerdictChip({ ai, size = 'md', conf = true }: { ai: AiVerdictSummary | { verdict: string | null; confidence: number | null }; size?: 'sm' | 'md'; conf?: boolean }) {
  if (!ai.verdict) return null;
  return (
    <span className={`verdict s-${verdictCls(ai.verdict)}${size === 'sm' ? ' sm' : ''}`} title={`AI verdict: ${verdictLabel(ai.verdict)}${ai.confidence !== null ? ` · ${pct(ai.confidence)} confidence` : ''}`}>
      {verdictLabel(ai.verdict).toUpperCase()}
      {conf && ai.confidence !== null && <span className="vc">{pct(ai.confidence)}</span>}
    </span>
  );
}

/** "AI triaging" / "Auto-resolved" pill for the agent's two states; nothing for the others. */
export function AiStatePill({ state, className = '' }: { state: string; className?: string }) {
  if (!AI_STATES.includes(state)) return null;
  return (
    <span className={`pill ai ${className}`} title={stateHint(state) ?? undefined}>
      <Icon name="spark" />
      {stateLabel(state)}
    </span>
  );
}

/** The violet line on a queue card (prototype .qai): verdict, confidence, and the paused marker. */
export function QueueAiLine({ e }: { e: EventSummary }) {
  const ai = e.ai;
  const aiState = AI_STATES.includes(e.state);
  if (!ai?.verdict && !e.agentPaused && !aiState) return null;
  return (
    <div className="qai">
      <Icon name="spark" />
      {ai?.verdict ? (
        <span className="qai-v">
          {verdictLabel(ai.verdict)}
          {ai.confidence !== null ? ` ${pct(ai.confidence)}` : ''}
        </span>
      ) : null}
      {aiState && <span className="qai-s">{ai?.verdict ? ' · ' : ''}{stateLabel(e.state)}</span>}
      {e.agentPaused && <span className="pill tiny-pill qai-p" title="The AI agent is paused on this event">agent paused</span>}
    </div>
  );
}

/* ---------- bits of the Agent tab ---------- */

function canPause(c: Ctl): boolean {
  const open = c.ev.state !== 'closed' && c.ev.state !== 'dispositioned';
  return open && (c.isSup || c.ev.owner?.id === c.meId);
}

const HIDDEN_NOTE = 'The AI is running in shadow mode at this site. Its assessment is hidden from operators while it is being measured.';

function HiddenNote({ compact }: { compact?: boolean }) {
  return (
    <div className={`aihidden${compact ? ' compact' : ''}`} role="note">
      <Icon name="eye" />
      <span>{HIDDEN_NOTE}</span>
    </div>
  );
}

function AState({ p, evState, title }: { p: AgentPanelFull; evState: string; title?: string }) {
  const settling = evState === 'settling';
  const working = !p.paused && !settling && (p.status === 'running' || p.status === 'waiting');
  const label = p.paused ? 'Agent paused' : settling ? 'Auto-resolved' : working ? (p.status === 'waiting' ? 'Agent waiting' : 'Agent working') : runStatusLabel(p.status);
  return (
    <span className={`astate${working ? '' : ' paused'}${p.status === 'failed' ? ' bad' : ''}`} title={title}>
      <i aria-hidden="true" />
      {label}
    </span>
  );
}

function LevelPill({ p }: { p: AgentPanelFull }) {
  return (
    <span className="pill ai" title={p.shadow ? 'Shadow: the agent works the event in parallel and records what it would have done' : 'The agent acts at this level'}>
      {levelLabel(p.liveLevel)}
      {p.shadowLevel !== null && p.shadowLevel !== p.liveLevel ? ` · L${p.shadowLevel} in shadow` : ''}
    </span>
  );
}

/** Snapshots of the alarms the agent looked at: its evidence frames (Agent tab; on the timeline they mark the alarm). */
function EvidenceFrames({ c, p }: { c: Ctl; p: AgentPanelFull }) {
  const seen: string[] = [];
  // The media its assessment cites come first (snapshot URLs end in the media id).
  for (const mid of p.assessment?.evidenceMediaIds ?? []) {
    const a = c.ev.alarms.find((x) => x.snapshotUrl?.endsWith(`/${mid}`));
    if (a && !seen.includes(a.id)) seen.push(a.id);
  }
  for (const t of p.toolCalls) {
    if (t.tool !== 'get_alarm_media' || t.decision !== 'allow') continue;
    const id = t.args?.alarmId;
    if (typeof id === 'string' && !seen.includes(id)) seen.push(id);
  }
  const frames = seen
    .map((id) => ({ id, i: c.ev.alarms.findIndex((a) => a.id === id) }))
    .filter((x) => x.i >= 0 && c.ev.alarms[x.i]?.snapshotUrl)
    .slice(0, 4);
  if (!frames.length) return null;
  return (
    <div className="frames" aria-label="Frames the agent looked at">
      {frames.map(({ id, i }) => {
        const a = c.ev.alarms[i]!;
        return (
          <button key={id} type="button" className="frame" onClick={() => c.selectAlarm(id)} title={`A${i + 1} · ${a.cameraName ?? 'camera'} · ${human(a.analytic, 'Alarm')}: the agent looked at this snapshot`}>
            <AuthImg url={a.snapshotUrl} alt="" lazy fallback="" />
            <span>A{i + 1}</span>
          </button>
        );
      })}
    </div>
  );
}

function PauseControl({ c, p, onDone }: { c: Ctl; p: AgentPanelFull; onDone?: () => void }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const allowed = canPause(c);
  const eventPaused = p.pausedScope === 'event' || c.ev.agentPaused === true;
  const go = async (pause: boolean) => {
    setBusy(true);
    try {
      if (pause) await api.pauseAgent(c.ev.id, reason.trim() || undefined);
      else await api.resumeAgent(c.ev.id);
      setAsking(false);
      setReason('');
      c.agent.reload();
      c.reload();
      onDone?.();
    } catch (e) {
      c.toast({ tone: 'bad', title: pause ? 'Could not pause the agent' : 'Could not resume the agent', body: errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  if (!allowed) return null;
  if (p.paused && !eventPaused) {
    return <span className="note">Paused for {p.pausedScope === 'global' ? 'all sites' : 'this site'} in Setup → AI.</span>;
  }
  if (eventPaused) {
    return (
      <button type="button" className="btn" onClick={() => void go(false)} disabled={busy}>
        <Icon name="play" />
        {busy ? 'Resuming…' : 'Resume agent'}
      </button>
    );
  }
  if (!asking) {
    return (
      <button type="button" className="btn" onClick={() => setAsking(true)} disabled={busy} title="Stops all agent actions on this event">
        <Icon name="pause" />
        Pause agent
      </button>
    );
  }
  return (
    <span className="pauseask" role="group" aria-label="Pause the agent">
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" aria-label="Reason" autoFocus onKeyDown={(e) => (e.key === 'Enter' ? void go(true) : e.key === 'Escape' ? (e.stopPropagation(), setAsking(false)) : undefined)} />
      <button type="button" className="btn pri" onClick={() => void go(true)} disabled={busy}>
        {busy ? 'Pausing…' : 'Pause'}
      </button>
      <button type="button" className="btn" onClick={() => setAsking(false)}>
        Cancel
      </button>
    </span>
  );
}

/** The site's pain points the agent matched on this event, violet (prototype intelHTML .chip.hit). */
export function PainChips({ p, all }: { p: AgentPanelFull | null; all?: string[] }) {
  const hits = p?.assessment?.painPoints ?? [];
  const list = all ?? hits;
  if (!list.length) return null;
  return (
    <div className="chips aipains" aria-label="Pain points">
      {list.map((x) => {
        const hit = hits.includes(x);
        return (
          <span key={x} className={`chip${hit ? ' aihit' : ''}`} title={hit ? 'Matched by the AI on this event' : undefined}>
            {hit && <Icon name="spark" />}
            {x}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Phase 5: the request in words with the site's device names ("AI asks to play 'Leave the property' on Fence East").
 * The arguments come with the approval, or from the panel's copy of it (an SSE approval has none).
 */
export function askText(c: Ctl, a: PendingApproval): string {
  const p = c.agent.data;
  const args = a.args ?? (p && p.visible ? approvalArgs(p.approvals?.find((x) => x.id === a.id)?.evidence) : null);
  return deviceAsk(a.tool, args, c.devices.data ? c.devices.names : null) ?? askWords(a.summary, a.tool);
}

/** The panel's pending requests when the co-pilot list does not have them (a supervisor's view, a missed message). */
export function pendingFromPanel(p: AgentPanelFull | null | undefined, now = Date.now()): Approval[] {
  return (p?.approvals ?? []).filter((a) => a.status === 'pending' && (Date.parse(a.expiresAt) || 0) > now);
}

/** Why the command box is off, or null when the agent takes instructions here. */
function commandOff(c: Ctl, p: AgentPanelFull): string | null {
  if (c.ev.state === 'closed' || c.ev.state === 'dispositioned') return 'The event is closed.';
  if (!c.isSup && c.ev.owner?.id !== c.meId) return 'Only the event owner or a supervisor can instruct the agent.';
  const accepts = p.acceptsInstructions ?? (!p.shadow && !p.paused && !['completed', 'failed', 'absent'].includes(p.status));
  if (accepts) return null;
  if (p.shadow || p.liveLevel < 1) return "This site's AI agent is in shadow mode (Level 0): it records what it would do, but doesn't take instructions.";
  if (p.paused) return `The agent is paused${p.pausedScope === 'global' ? ' for all sites' : p.pausedScope === 'site' ? ' for this site' : ' on this event'}. Resume it to give it instructions.`;
  if (['completed', 'failed', 'absent'].includes(p.status)) return `The agent isn't running on this event (${runStatusLabel(p.status).toLowerCase()}).`;
  return "The agent isn't taking instructions right now.";
}

/* ---------- the Agent tab ---------- */

const KIND_ICON: Record<string, IconName> = { observe: 'eye', assess: 'spark', act: 'bolt', evaluate: 'check', handoff: 'send', message: 'spark' };
const TOOL_ICON: Record<string, IconName> = {
  get_event: 'list',
  site_context: 'map',
  site_history: 'chart',
  get_alarm_media: 'cam',
  get_frames: 'cam',
  attach_note: 'note',
  record_assessment: 'spark',
  propose_disposition: 'tag',
  heartbeat_extend: 'log',
  escalate: 'send',
  resolve_event: 'check',
  set_watch: 'eye',
  cancel_watch: 'eye',
  get_watch_hit: 'cam',
  confirm_watch_hit: 'eye',
  reply_to_operator: 'spark',
  draft_dispatch: 'list',
  draft_report: 'list',
  complete_mission_step: 'check',
  get_devices: 'speaker',
  talk_down: 'speaker',
  trigger_relay: 'bolt',
  notify_customer: 'mail',
};

function DecisionTag({ t }: { t: AgentToolCall }) {
  if (t.decision === 'shadow') return <span className="tagx shadow" title="Level 0: recorded, not acted on">would have</span>;
  if (t.decision === 'deny') return <span className="tagx deny">denied</span>;
  if (t.decision === 'ask') return <span className="tagx">asked</span>;
  if (t.status === 'failed') return <span className="tagx deny">failed</span>;
  return <span className="tagx allow">allowed</span>;
}

type Extra = { k: 'ask'; at: number; a: Approval } | { k: 'cmd'; at: number; i: Instruction };

function Trace({ c: ctl, p, tz, meId }: { c: Ctl; p: AgentPanelFull; tz: string; meId: string }) {
  const names = ctl.devices.data ? ctl.devices.names : null;
  // Steps and tool calls (with their turn dividers), plus what people asked of the agent and how its requests ended.
  const base: (TraceRow | Extra)[] = mergeTrace(p.steps, p.toolCalls);
  const extra: Extra[] = [
    ...(p.approvals ?? []).map((a) => ({ k: 'ask' as const, at: Date.parse(a.requestedAt ?? a.expiresAt) || 0, a })),
    ...(p.instructions ?? []).map((i) => ({ k: 'cmd' as const, at: Date.parse(i.at) || 0, i })),
  ];
  const rows = [...base.map((r, n) => ({ r, n })), ...extra.map((r, n) => ({ r, n: base.length + n }))]
    .sort((x, y) => x.r.at - y.r.at || x.n - y.n)
    .map((x) => x.r)
    .reverse();
  if (!rows.length) return <div className="note">Nothing yet.</div>;
  const out: ReactNode[] = [];
  for (const r of rows) {
    const t = fmtTime(r.at, tz);
    if (r.k === 'ask') {
      const a = r.a;
      out.push(
        <div key={`a-${a.id}`} className={`tr k-ask a-${a.status}`}>
          <span className="mono dim">{t}</span>
          <Icon name="alert" />
          <div className="trc">
            <span className="tword">{deviceAsk(a.tool, approvalArgs(a.evidence), names) ?? askWords(a.summary, a.tool)}</span>
            <span className={`tagx${a.status === 'approved' ? ' allow' : a.status === 'pending' ? '' : ' deny'}`}>{APPROVAL_STATUS_LABEL[a.status] ?? a.status}</span>
            {a.decidedAt && a.status !== 'pending' && (
              <div className="txt">
                {a.status === 'expired' ? 'No answer in 20 s' : a.decidedBy ? (a.decidedBy === meId ? 'By you' : 'By a supervisor') : 'By the system'} · {fmtTime(a.decidedAt, tz)}
                {a.note ? ` · “${a.note}”` : ''}
              </div>
            )}
            <div className="tool">{a.tool}</div>
          </div>
        </div>,
      );
      continue;
    }
    if (r.k === 'cmd') {
      const i = r.i;
      out.push(
        <div key={`i-${i.id}`} className="tr k-cmd">
          <span className="mono dim">{t}</span>
          <Icon name="users" />
          <div className="trc">
            <span className="txt">
              {i.userId === meId ? 'You' : (i.userName ?? 'Operator')}: {i.text}
            </span>
          </div>
        </div>,
      );
      continue;
    }
    if (r.k === 'tool') {
      const c = r.call;
      const why = c.decision === 'deny' || c.decision === 'shadow' || c.decision === 'ask' ? reasonWords(c.reason, c.tool) : null;
      const line = toolResultLine(c, names);
      out.push(
        <div key={`t-${c.id}`} className={`tr k-tool d-${c.decision}`}>
          <span className="mono dim">{t}</span>
          <Icon name={TOOL_ICON[c.tool] ?? 'gear'} />
          <div className="trc">
            <span className="tword">{callWords(c)}</span>
            <DecisionTag t={c} />
            {why && <span className="why"> · {why}</span>}
            {line && <div className="txt">{line}</div>}
            <div className="tool">{c.tool}</div>
          </div>
        </div>,
      );
    } else {
      const s = r.step;
      out.push(
        <div key={`s-${s.seq}`} className={`tr k-step k-${s.kind}`}>
          <span className="mono dim">{t}</span>
          <Icon name={KIND_ICON[s.kind] ?? 'spark'} />
          <div className="trc">
            <span className="kind">{STEP_KIND_LABEL[s.kind] ?? human(s.kind)}</span>
            {s.shadow && <span className="tagx shadow">shadow</span>}
            {s.rationale && <span className="txt"> {s.rationale}</span>}
          </div>
        </div>,
      );
    }
    // Newest first: the turn divider sits under the first row of its turn.
    if (r.turn) {
      out.push(
        <div key={`turn-${r.k}-${r.k === 'step' ? r.step.seq : r.call.id}`} className="trturn">
          <span>{r.turn}</span>
        </div>,
      );
    }
  }
  return <div className="trace">{out}</div>;
}

function Usage({ p }: { p: AgentPanelFull }) {
  const u = p.usage;
  const b = p.budget;
  const of = (v: number | undefined, max: number | undefined) => `${v ?? 0}${max ? ` / ${max}` : ''}`;
  const secs = (u.wall_ms ?? 0) / 1000;
  return (
    <div className="sec">
      <div className="lbl">
        <span>Usage</span>
        <span className="dim">{p.model ? `${p.model.name} · ${p.model.model}` : 'model unknown'}</span>
      </div>
      <div className="aiuse">
        <div>
          <span className="lbl">Model calls</span>
          <b>{of(u.model_calls, b.max_model_calls)}</b>
        </div>
        <div>
          <span className="lbl">Tool calls</span>
          <b>{of(p.toolCalls.length, b.max_tool_calls)}</b>
        </div>
        <div>
          <span className="lbl">Cost</span>
          <b>{money(u.cost ?? 0)}</b>
          {b.max_cost ? <small>of {money(b.max_cost)}</small> : null}
        </div>
        <div>
          <span className="lbl">Time</span>
          <b>{secs < 10 ? secs.toFixed(1) : Math.round(secs)} s</b>
          {b.max_wall_s ? <small>of {b.max_wall_s} s</small> : null}
        </div>
        <div>
          <span className="lbl">Frames</span>
          <b>{of(u.frames, b.max_frames)}</b>
        </div>
        <div>
          <span className="lbl">Tokens</span>
          <b>{((u.tokens_in ?? 0) + (u.tokens_out ?? 0)).toLocaleString()}</b>
          <small>
            {(u.tokens_in ?? 0).toLocaleString()} in · {(u.tokens_out ?? 0).toLocaleString()} out
          </small>
        </div>
      </div>
      {p.model?.provider === 'simulator' && <div className="note">Simulated model: verdicts come from rules, not from a real model.</div>}
      {p.error && <div className="note bad-t">Run error: {p.error}</div>}
    </div>
  );
}

export function AgentTab({ c }: { c: Ctl }) {
  const a = c.agent;
  const p = a.data;
  const tz = c.ev.siteTimeZone;
  if (p === undefined) {
    return a.error ? (
      <div className="sec">
        <div className="note bad-t">Could not load the AI agent: {errorText(a.error)}</div>
        <button type="button" className="btn" onClick={a.reload}>
          <Icon name="sync" />
          Retry
        </button>
      </div>
    ) : (
      <div className="note">
        <span className="spinner" aria-hidden="true" /> Loading the agent…
      </div>
    );
  }
  if (p === null) {
    return (
      <>
        <div className="note">No AI agent run for this event.</div>
        <WatchList c={c} />
      </>
    );
  }
  if (!p.visible) {
    return (
      <>
        <HiddenNote />
        <WatchList c={c} />
      </>
    );
  }
  if (p.status === 'absent') {
    return (
      <>
        <div className="sec">
          <div className="note">The AI agent did not run on this event. It went straight to an operator (agent off, always-escalate alarm, human-review-only or commissioning site, paused, or Level 0 with the runtime absent). The event log says which.</div>
        </div>
        <WatchList c={c} />
      </>
    );
  }
  const closed = c.ev.state === 'closed' || c.ev.state === 'dispositioned';
  return (
    <>
      <div className="sec">
        <div className="row-wrap">
          <AState p={p} evState={c.ev.state} />
          <LevelPill p={p} />
          <span className="sp" />
          {!closed && <PauseControl c={c} p={p} />}
        </div>
        <div className="note">
          Now: <span className="fg">{nowLine(p, c.ev.state)}</span>
        </div>
        {p.paused && p.pausedScope === 'event' && <div className="note">Paused on this event. The agent takes no actions here until it is resumed.</div>}
      </div>

      {c.approvals.map((a, i) => (
        <ApprovalCard key={a.id} a={a} keys={i === c.approvals.length - 1} text={askText(c, a)} />
      ))}

      <CommandBox c={c} p={p} />

      <WatchList c={c} />

      <div className="sec aiverdict">
        <div className="lbl">
          <span>Assessment</span>
          {p.shadow && <span className="dim">shadow · operators do not see this at Level 0</span>}
        </div>
        <div className="row-wrap">
          {p.verdict ? <VerdictChip ai={{ verdict: p.verdict, confidence: p.confidence }} conf={false} /> : <span className="muted">No verdict yet</span>}
          {p.confidence !== null && <span className="conf">{pct(p.confidence)} confidence</span>}
          {p.wouldResolve !== null && (
            <span className="pill ai" title="Whether the agent would close this event without a human">
              {p.wouldResolve ? (p.shadow ? 'would have auto-resolved' : 'auto-resolves') : 'needs a human'}
            </span>
          )}
        </div>
        {/* One story (the user, 2026-10-29: the assessment and the handoff summary said the same thing): the handoff
            summary when there is one, else the assessment's; the agent's reasoning and the disposition note fold under
            "Why". Everything stays in the event's record and the trace. */}
        {(p.handoffSummary || p.summary) && <div className="aitext">{p.handoffSummary || p.summary}</div>}
        {p.proposedDisposition && (
          <div className="aitext">
            <span className="lbl">Proposed disposition</span> <b className="mono">{p.proposedDisposition.code}</b>
          </div>
        )}
        {((p.handoffSummary && p.summary && p.summary !== p.handoffSummary) || p.proposedDisposition?.notes) && (
          <details className="aiwhy">
            <summary>Why</summary>
            {p.handoffSummary && p.summary && p.summary !== p.handoffSummary && <div className="aitext">{p.summary}</div>}
            {p.proposedDisposition?.notes && <div className="aitext dim">{p.proposedDisposition.notes}</div>}
          </details>
        )}
        <EvidenceFrames c={c} p={p} />
        {(p.assessment?.painPoints.length ?? 0) > 0 && (
          <>
            <div className="lbl">Matched pain points</div>
            <PainChips p={p} />
          </>
        )}
        {(p.assessment?.subjects.length ?? 0) > 0 && (
          <>
            <div className="lbl">Subjects</div>
            <ul className="aisubj">
              {p.assessment!.subjects.map((x, i) => (
                <li key={i}>
                  {x.description ?? 'Subject'}
                  {x.location ? <span className="dim"> · {x.location}</span> : null}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Drafts c={c} p={p} />

      <div className="sec">
        <div className="lbl">
          <span>What the agent did · {plural(p.steps.length, 'step')} · {plural(p.toolCalls.length, 'tool call')}</span>
          <span className="dim">tool calls are audited</span>
        </div>
        <div className="note">
          Newest first, site time ({tzAbbr(tz)}).
          {p.startedAt ? ` Started ${fmtTime(p.startedAt, tz)}` : ''}
          {p.endedAt ? `, ended ${fmtTime(p.endedAt, tz)}.` : p.startedAt ? '.' : ''}
        </div>
        <Trace c={c} p={p} tz={tz} meId={c.meId} />
      </div>

      <Usage p={p} />

      <div className="note">
        {c.canAct && !p.shadow ? `You have control of this event, so the agent asks before it checks off mission steps${p.liveLevel >= 2 ? ' or uses speakers and relays' : ''}. ` : ''}The agent acts only through the tool API, which checks every call. It never dispatches police, calls 911, unlocks gates or disarms a site.
      </div>
    </>
  );
}

/* ---------- Phase 4: tell the agent ---------- */

/** "Tell the agent" (prototype agentPanelHTML .cmd + .qchips) and the conversation from the panel's instructions. */
function CommandBox({ c, p }: { c: Ctl; p: AgentPanelFull }) {
  const ev = c.ev;
  const [text, setText] = useDraft(c.drafts, ev.id, 'agent.cmd', '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement | null>(null);
  const off = commandOff(c, p);
  useEffect(() => {
    if (c.cmdFocus && !off) ref.current?.focus();
  }, [c.cmdFocus, off]);
  const send = async (t: string) => {
    const msg = t.trim();
    if (!msg || off || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.instruct(ev.id, msg);
      setText('');
      c.agent.reload();
    } catch (e) {
      // 409: shadow site, paused, closed, or the agent isn't running. The server says which.
      setErr(e instanceof ApiError ? e.message : errorText(e));
      if (e instanceof ApiError && e.status === 409) c.agent.reload();
    } finally {
      setBusy(false);
    }
  };
  const list = (p.instructions ?? []).slice().sort((a, b) => (Date.parse(a.at) || 0) - (Date.parse(b.at) || 0));
  return (
    <div className="sec">
      <div className="lbl ai-t">
        <span>Tell the agent</span>
        {list.length > 0 && <span className="dim">{plural(list.length, 'instruction')}</span>}
      </div>
      <form
        className="cmd"
        onSubmit={(e) => {
          e.preventDefault();
          void send(text);
        }}
      >
        <input
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={off ? 'Not taking instructions' : 'e.g. watch Cam 01 for police and raise me'}
          aria-label="Instruction for the agent"
          autoComplete="off"
          maxLength={1000}
          disabled={!!off}
          onKeyDown={(e) => e.key === 'Escape' && (e.currentTarget.blur(), e.stopPropagation())}
        />
        <button type="submit" className="btn aib" disabled={!!off || busy || !text.trim()} aria-label="Send to the agent" title="Send (Enter)">
          {busy ? <span className="spinner" aria-hidden="true" /> : <Icon name="send" />}
        </button>
      </form>
      <div className="qchips" role="group" aria-label="Quick instructions">
        {QUICK_INSTRUCTIONS.map((q) => (
          <button key={q} type="button" onClick={() => void send(q)} disabled={!!off || busy}>
            {q}
          </button>
        ))}
      </div>
      {off && <div className="note">{off}</div>}
      {err && (
        <div className="inline-err" role="alert">
          <Icon name="alert" />
          {err}
        </div>
      )}
      {list.length > 0 && <Conversation list={list} meId={c.meId} tz={ev.siteTimeZone} />}
    </div>
  );
}

function Conversation({ list, meId, tz }: { list: Instruction[]; meId: string; tz: string }) {
  return (
    <div className="convo" aria-label="Conversation with the agent" aria-live="polite">
      {list.slice(-6).map((i) => {
        const open = i.status === 'pending' || i.status === 'working';
        return (
          <div key={i.id} className="cturn">
            <div className="cmsg you">
              <span className="who">{i.userId === meId ? 'You' : (i.userName ?? 'Operator')}</span>
              <span className="ctext">{i.text}</span>
              <span className="mono dim">{fmtTime(i.at, tz)}</span>
              <span className={`cst s-${i.status}`}>{INSTRUCTION_STATUS_LABEL[i.status] ?? i.status}</span>
            </div>
            {i.reply ? (
              <div className={`cmsg ai${i.status === 'failed' ? ' failed' : ''}`}>
                <Icon name="spark" />
                <span className="ctext">{i.reply}</span>
                {i.repliedAt && <span className="mono dim">{fmtTime(i.repliedAt, tz)}</span>}
              </div>
            ) : open ? (
              <div className="cmsg ai working">
                <span className="spinner" aria-hidden="true" />
                <span className="ctext">Working on it…</span>
              </div>
            ) : null}
          </div>
        );
      })}
      {list.length > 6 && <div className="note">Older instructions are in the trace below.</div>}
    </div>
  );
}

/** What the agent drafted (dispatch description, customer report): used in the Dispatch and Disposition drawers. */
function Drafts({ c, p }: { c: Ctl; p: AgentPanelFull }) {
  const d = p.drafts;
  if (!d?.dispatch && !d?.report) return null;
  const tz = c.ev.siteTimeZone;
  return (
    <div className="sec">
      <div className="lbl">
        <span>Drafts</span>
        <span className="dim">check before you use them</span>
      </div>
      {d.dispatch && (
        <div className="aidraft">
          <span className="lbl ai-t">
            <Icon name="spark" /> Dispatch description{d.dispatch.createdAt ? ` · ${fmtTime(d.dispatch.createdAt, tz)}` : ''}
          </span>
          <span className="small">{d.dispatch.summary || d.dispatch.subjects.map((x) => x.description).filter(Boolean).join('; ')}</span>
          <button type="button" className="btn sm self-start" onClick={() => c.openDrawer({ k: 'dispatch' })} disabled={!c.canAct}>
            <Icon name="shield" />
            Open in Dispatch <kbd>D</kbd>
          </button>
        </div>
      )}
      {d.report && (
        <div className="aidraft">
          <span className="lbl ai-t">
            <Icon name="spark" /> Customer report{d.report.createdAt ? ` · ${fmtTime(d.report.createdAt, tz)}` : ''}
          </span>
          <span className="small">{d.report.narrative}</span>
          <button type="button" className="btn sm self-start" onClick={() => c.openDrawer({ k: 'disposition' })} disabled={!c.canAct}>
            <Icon name="check" />
            Use in Disposition <kbd>C</kbd>
          </button>
        </div>
      )}
    </div>
  );
}
